import { useId, useState, type FormEvent } from "react";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../api/client";
import type { PmProject } from "../api/pm";
import type {
  ImputationRule,
  ObservationsShadowResponse,
  RuleMatchField,
  UnassignedTimeResponse,
} from "../api/types";
import { invalidateReimputationReads } from "../hooks/useActivities";
import {
  useCreateImputationRule,
  useDeleteImputationRule,
  useImputationRules,
} from "../hooks/useImputationRules";
import { useManagerTeam } from "../hooks/useManagerTeam";
import { observationsShadowQuery } from "../hooks/useObservationsShadow";
import { useProjects } from "../hooks/usePm";
import { unassignedTimeQuery } from "../hooks/useUnassignedTime";
import { useSelfUrl } from "../lib/auth/useSelfUrl";
import {
  chantierPath,
  MAX_SHADOW_SPAN_DAYS,
  resolveRepoProjects,
} from "../lib/chantiers";
import { localDayKey, localMidnight } from "../lib/date";
import { formatMinutes } from "../lib/format";
import { createRuleAndApply, type RuleApplied } from "../lib/imputationRules";
import { useUiStore } from "../state/uiStore";
import { Sheet } from "./Sheet";

const LABEL_MAX = 200;
/** Borne de /workers/unassigned-time : trois ans de temps sans projet. */
const OBSERVED_DAYS = 1095;
/** Le temps passé se rattache an par an, cinq ans au plus. */
const APPLY_WINDOWS = 5;

interface ObservedSource {
  key: string;
  field: RuleMatchField;
  label: string;
  /** Chantier : la branche sans préfixe utilisateur, vide pour tout le dépôt
   * ou le site. */
  branch: string;
  /** Une branche observée du chantier, pour lire où part son temps. */
  sample: string;
  minutes: number;
  /** Code du projet où part ce temps aujourd'hui, null sans règle de tous. */
  current: string | null;
  /** Une règle de tous porte déjà exactement cette source. */
  taken: boolean;
}

function sourceKey(field: string, value: string, branch = ""): string {
  const key = `${field}:${value.trim().toLowerCase()}`;
  return branch ? `${key}#${branch}` : key;
}

/** Motif d'un chantier : « * » couvre tous les préfixes utilisateur, et la
 * branche sans préfixe. */
function chantierPattern(path: string): string {
  return `*${path}`;
}

function patternPath(pattern: string): string {
  return pattern.replace(/^\*/, "");
}

function sourceName(field: string, label: string, branch = ""): string {
  if (branch) return `${label}, chantier ${patternPath(branch)}`;
  return field === "domain" ? `Site ${label}` : label;
}

/** Chantier d'une branche, quand elle en a un qui s'écrit tel quel en motif :
 * le serveur lit « * » et « ? » comme jokers, sans échappement, et git les
 * refuse dans un nom de branche. null sinon. */
function chantierOf(branch: string): string | null {
  return /[*?]/.test(branch) ? null : chantierPath(branch);
}

/** Dépôts, chantiers (branches) et sites observés, tous salariés confondus :
 * le temps sans projet, puis les dépôts et branches du dernier mois, même
 * déjà imputés. Chaque entrée dit vers quel projet son temps part aujourd'hui,
 * selon les règles de tous ; celle qu'une règle de tous porte déjà ne se
 * choisit plus. */
function observedSources(
  unassigned: (UnassignedTimeResponse | undefined)[],
  shadows: (ObservationsShadowResponse | undefined)[],
  rules: ImputationRule[],
  projects: PmProject[],
): ObservedSource[] {
  const tenantRules = rules.filter((r) => !r.worker_url);
  const ruleOf = new Map(
    tenantRules.map((r) => [
      sourceKey(
        r.match_field,
        r.match_value,
        patternPath(r.branch_pattern ?? ""),
      ),
      r,
    ]),
  );
  const codeOf = new Map(projects.map((p) => [p.id, p.code]));
  const projectOf = resolveRepoProjects(tenantRules, projects, "");
  const byKey = new Map<string, ObservedSource>();
  const add = (
    field: RuleMatchField,
    label: string,
    branch: string,
    sample: string,
    minutes: number,
  ) => {
    const key = sourceKey(field, label, branch);
    let s = byKey.get(key);
    if (!s) {
      const rule = ruleOf.get(key);
      const resolved = field === "repo" ? projectOf(label, sample) : null;
      const projectId =
        resolved?.projectId ?? (rule?.active ? rule.project_id : null);
      s = {
        key,
        field,
        label,
        branch,
        sample,
        minutes: 0,
        current: projectId
          ? codeOf.get(projectId) ||
            resolved?.name ||
            rule?.project_label ||
            `Projet ${projectId.slice(0, 8)}`
          : null,
        taken: !!rule,
      };
      byKey.set(key, s);
    }
    s.minutes += minutes;
  };
  for (const res of unassigned) {
    for (const l of res?.by_label ?? []) {
      if (l.label) add(l.field, l.label, "", "", l.worked_minutes);
    }
    for (const a of res?.activities ?? []) {
      const repo = a.context_repo?.trim() ?? "";
      const branch = a.context_branch?.trim() ?? "";
      const path = chantierOf(branch);
      if (repo && path) add("repo", repo, path, branch, a.worked_minutes);
    }
  }
  for (const res of shadows) {
    for (const day of res?.days ?? []) {
      for (const r of day.repos) {
        if (!r.repo) continue;
        add("repo", r.repo, "", "", 0);
        for (const b of r.branches ?? []) {
          const path = chantierOf(b.branch);
          if (path) add("repo", r.repo, path, b.branch, 0);
        }
      }
    }
  }
  const sources = [...byKey.values()];
  const wholeMinutes = new Map(
    sources.filter((s) => !s.branch).map((s) => [s.key, s.minutes]),
  );
  const parentOf = (s: ObservedSource) => sourceKey(s.field, s.label);
  return sources.sort((a, b) => {
    const pa = parentOf(a);
    const pb = parentOf(b);
    if (pa !== pb) {
      const ma = wholeMinutes.get(pa) ?? 0;
      const mb = wholeMinutes.get(pb) ?? 0;
      return mb - ma || pa.localeCompare(pb);
    }
    if (!a.branch !== !b.branch) return a.branch ? 1 : -1;
    return b.minutes - a.minutes || a.branch.localeCompare(b.branch);
  });
}

function sourceOption(s: ObservedSource): string {
  return [
    sourceName(s.field, s.label, s.branch),
    s.minutes > 0 ? `${formatMinutes(s.minutes)} sans projet` : "",
    s.current ? `aujourd'hui : ${s.current}` : "",
  ]
    .filter(Boolean)
    .join(" · ");
}

function appliedText(result: RuleApplied): string {
  if (!result.complete || !result.applied) {
    return "Le temps à venir sera imputé d'office ; le temps passé le sera depuis Règles d'imputation.";
  }
  const stat = result.applied.rules.find((r) => r.rule_id === result.rule.id);
  const done = `${formatMinutes(stat?.worked_minutes ?? 0)} imputées, la suite le sera d'office`;
  return stat && stat.skipped_locked > 0
    ? `${done}. ${formatMinutes(stat.skipped_locked_minutes)} restent dans des feuilles déjà soumises.`
    : done;
}

function AddSourceSheet({
  projectId,
  projectName,
  rules,
  onClose,
}: {
  projectId: string;
  projectName: string;
  rules: ImputationRule[];
  onClose: () => void;
}) {
  const [choice, setChoice] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [range] = useState(() => {
    const today = new Date();
    return {
      from: localDayKey(localMidnight(today, -OBSERVED_DAYS)),
      recent: localDayKey(localMidnight(today, -MAX_SHADOW_SPAN_DAYS)),
      to: localDayKey(today),
    };
  });
  const formId = useId();
  const fieldId = useId();
  const selfUrl = useSelfUrl();
  const team = useManagerTeam();
  const workers = [
    ...new Set([
      ...(selfUrl ? [selfUrl] : []),
      ...(team.data?.workers ?? []).map((w) => w.worker_url),
    ]),
  ];
  const observed = useQueries({
    queries: workers.map((w) => unassignedTimeQuery(w, range.from, range.to)),
  });
  const shadows = useQueries({
    queries: workers.map((w) =>
      observationsShadowQuery(w, range.recent, range.to),
    ),
  });
  const projects = useProjects();
  const create = useCreateImputationRule();
  const qc = useQueryClient();
  const pushToast = useUiStore((s) => s.pushToast);

  const sources = observedSources(
    observed.map((q) => q.data),
    shadows.map((q) => q.data),
    rules,
    projects.data?.items ?? [],
  );
  const loading =
    team.isLoading ||
    projects.isLoading ||
    observed.some((q) => q.isLoading) ||
    shadows.some((q) => q.isLoading);
  const failed =
    team.isError || (observed.length > 0 && observed.every((q) => q.isError));
  const source = sources.find((s) => s.key === choice && !s.taken);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (pending || !source) return;
    setError(null);
    setPending(true);
    let result: RuleApplied;
    try {
      result = await createRuleAndApply(
        create.mutateAsync,
        {
          scope: "tenant",
          worker_url: null,
          match_field: source.field,
          match_value:
            source.field === "domain"
              ? source.label.toLowerCase()
              : source.label,
          ...(source.branch
            ? { branch_pattern: chantierPattern(source.branch) }
            : {}),
          project_id: projectId,
          ...(projectName
            ? { project_label: projectName.slice(0, LABEL_MAX) }
            : {}),
        },
        APPLY_WINDOWS,
      );
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 409
          ? source.branch
            ? "Ce chantier est déjà rattaché à un projet."
            : "Ce dépôt ou ce site est déjà rattaché à un projet."
          : err instanceof ApiError && err.status === 403
            ? "Seul un administrateur peut rattacher un dépôt pour tous les salariés."
            : "Rattachement impossible pour le moment. Réessayez dans un instant.",
      );
      return;
    } finally {
      setPending(false);
    }
    invalidateReimputationReads(qc);
    pushToast({
      variant: "info",
      title: `${sourceName(source.field, source.label, source.branch)} rattaché à ${projectName || "ce projet"}`,
      subtitle: appliedText(result),
    });
    onClose();
  }

  return (
    <Sheet
      title="Ajouter un dépôt ou un site"
      onClose={onClose}
      footer={
        <div className="sheet-cta">
          <button
            type="submit"
            form={formId}
            className="primary"
            disabled={pending || !source}
          >
            {pending ? "Rattachement…" : "Rattacher"}
          </button>
        </div>
      }
    >
      <p className="form-hint">
        Tout le temps passé dans ce dépôt, sur ce chantier ou sur ce site ira à
        ce projet, pour tous les salariés : celui déjà pointé, sur cinq ans au
        plus, comme celui à venir. Un chantier reprend le temps que la règle de
        son dépôt avait rattaché pour tous. Les règles personnelles et les
        corrections à la main ne bougent pas, les feuilles déjà soumises non
        plus.
      </p>
      <form
        id={formId}
        className="inline-form reimpute-form"
        onSubmit={(e) => void handleSubmit(e)}
      >
        <label htmlFor={fieldId}>
          Dépôt, chantier ou site
          <select
            id={fieldId}
            value={choice}
            onChange={(e) => setChoice(e.target.value)}
            required
          >
            <option value="">
              {loading
                ? "Chargement des dépôts et sites…"
                : failed
                  ? "Dépôts et sites indisponibles"
                  : sources.length === 0
                    ? "Aucun dépôt, chantier ni site observé"
                    : "Choisir un dépôt, un chantier ou un site"}
            </option>
            {sources.map((s) => (
              <option key={s.key} value={s.key} disabled={s.taken}>
                {sourceOption(s)}
              </option>
            ))}
          </select>
        </label>
        {error && (
          <p className="reimpute-error" role="alert">
            {error}
          </p>
        )}
      </form>
    </Sheet>
  );
}

/** Dépôts, chantiers et sites du projet : les règles d'imputation qui le
 * désignent. Un ajout vaut pour tous les salariés et rattache aussi le temps
 * passé. Réservé
 * à l'admin, seul autorisé par TAWA à poser une règle de tous. */
export function ProjectSources({
  projectId,
  projectName,
}: {
  projectId: string;
  projectName: string;
}) {
  const titleId = useId();
  const rules = useImputationRules();
  const remove = useDeleteImputationRule();
  const pushToast = useUiStore((s) => s.pushToast);
  const [adding, setAdding] = useState(false);
  const all = rules.data?.rules ?? [];
  const mine = all.filter((r) => r.project_id === projectId);

  function handleRemove(r: ImputationRule) {
    remove.mutate(r.id, {
      onSuccess: () =>
        pushToast({
          variant: "info",
          title: `${sourceName(r.match_field, r.match_value, r.branch_pattern)} retiré du projet`,
          subtitle: "Le temps déjà imputé reste au projet.",
        }),
    });
  }

  return (
    <section className="project-sources" aria-labelledby={titleId}>
      <h2 id={titleId} className="section-label">
        Dépôts et sites
      </h2>
      {rules.isLoading ? (
        <div className="empty-state" role="status">
          Chargement…
        </div>
      ) : rules.isError ? (
        <div className="empty-state" role="alert">
          Dépôts et sites indisponibles.
        </div>
      ) : mine.length === 0 ? (
        <div className="empty-state">
          Aucun dépôt ni site rattaché à ce projet.
        </div>
      ) : (
        <ul className="settings-list rules-list" aria-label="Dépôts et sites">
          {mine.map((r) => (
            <li
              key={r.id}
              className={r.active ? "set-row" : "set-row inactive"}
            >
              <span className="text">
                <span className="lbl">
                  {sourceName(r.match_field, r.match_value, r.branch_pattern)}
                </span>
                <span className="sub">
                  {r.worker_url ? "Un salarié" : "Tous les salariés"}
                  {r.active ? "" : " · désactivée"}
                </span>
              </span>
              <button
                type="button"
                className="pill-btn danger"
                aria-label={`Retirer ${sourceName(r.match_field, r.match_value, r.branch_pattern)}`}
                onClick={() => handleRemove(r)}
                disabled={remove.isPending}
              >
                Retirer
              </button>
            </li>
          ))}
        </ul>
      )}
      {remove.isError && (
        <p className="billing-note" role="alert">
          Retrait impossible pour le moment. Réessayez dans un instant.
        </p>
      )}
      <div className="rules-actions">
        <button
          type="button"
          className="pill-btn"
          onClick={() => setAdding(true)}
          disabled={!rules.isSuccess}
        >
          Ajouter un dépôt ou un site
        </button>
      </div>
      {adding && (
        <AddSourceSheet
          projectId={projectId}
          projectName={projectName}
          rules={all}
          onClose={() => setAdding(false)}
        />
      )}
    </section>
  );
}

export default ProjectSources;
